import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import {
    FaImages, FaTrash, FaSignOutAlt, FaArrowLeft, FaPlus,
    FaCheckCircle, FaExclamationCircle, FaInfoCircle,
    FaUpload, FaEdit, FaSyncAlt
} from 'react-icons/fa'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { RoleProvider, useRole } from '../../hooks/useRole'
import { useVisibilityRefresh } from '../../hooks/useVisibilityRefresh'
import ServiceHealthBar from '../../components/ServiceHealthBar'
import GalleryModal from '../../components/GalleryModal'
import {
    useGalleries,
    fetchGalleryImages,
    uploadGalleryImage,
    deleteGalleryImage,
    deleteGallery,
    replaceGalleryImage,
    updateGalleryLabel,
    createGallery
} from '../../lib/galleries'

const KNOWN_SERVICES = [
    { key: 'pergola',      label: 'Pérgolas' },
    { key: 'fence',        label: 'Cerca' },
    { key: 'concrete',     label: 'Concreto' },
    { key: 'roofing',      label: 'Techo' },
    { key: 'windows',      label: 'Ventanas' },
    { key: 'architecture', label: 'Arquitectura' },
]

export default function GalleryEditorContainer() {
    const [session, setSession] = useState(null)

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => setSession(session))
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setSession(session))
        return () => subscription.unsubscribe()
    }, [])

    if (!session) return <GalleryEditorContent session={session} setSession={setSession} />

    return (
        <RoleProvider session={session}>
            <GalleryEditorContent session={session} setSession={setSession} />
        </RoleProvider>
    )
}

function GalleryEditorContent({ session, setSession }) {
    const { t } = useTranslation()
    const { permissions, loading: roleLoading } = useRole()
    const navigate = useNavigate()

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [authLoading, setAuthLoading] = useState(false)
    const [honeyPot, setHoneyPot] = useState('')

    const [toast, setToast] = useState({ show: false, message: '', type: 'info' })
    const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null })

    const [selectedGallery, setSelectedGallery] = useState(null)
    const [images, setImages] = useState([])
    const [imagesLoading, setImagesLoading] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [createLoading, setCreateLoading] = useState(false)
    const [dragActive, setDragActive] = useState(false)

    const [editingLabelId, setEditingLabelId] = useState(null)
    const [editingLabelValue, setEditingLabelValue] = useState('')

    const fileInputRef = useRef(null)
    const replaceFileInputRef = useRef(null)
    const [replacingImageId, setReplacingImageId] = useState(null)

    const { galleries, loading: galleriesLoading, refresh: refreshGalleries, silentReload: silentReloadGalleries } = useGalleries()
    const markGalleryRefreshedRef = useRef(null)

    // Silent background refresh when tab becomes visible (guard while uploading)
    const { markRefreshed: markGalleryRefreshed } = useVisibilityRefresh({
        staleMs: 300_000,
        onRefresh: useCallback(() => {
            if (uploading || imagesLoading || createLoading) {
                markGalleryRefreshedRef.current?.()
                return
            }
            Promise.resolve(silentReloadGalleries()).finally(() => markGalleryRefreshedRef.current?.())
        }, [uploading, imagesLoading, createLoading, silentReloadGalleries]),
    })
    markGalleryRefreshedRef.current = markGalleryRefreshed

    const [creatingGallery, setCreatingGallery] = useState(false)
    const [selectedServiceKey, setSelectedServiceKey] = useState('')

    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewIndex, setPreviewIndex] = useState(0)

    const availableServices = KNOWN_SERVICES.filter(
        s => !galleries.some(g => g.service_key === s.key)
    )

    const handleCreateGallery = async () => {
        const svc = KNOWN_SERVICES.find(s => s.key === selectedServiceKey)
        if (!svc) { showToast(t('admin.gallery.error.key_empty'), 'error'); return }
        setCreateLoading(true)
        try {
            const created = await createGallery(svc.key, svc.label)
            refreshGalleries()
            setSelectedGallery(created)
            setSelectedServiceKey('')
            setCreatingGallery(false)
            showToast(t('admin.gallery.success.created'), 'success')
        } catch (err) {
            const isDup = err.message?.includes('unique') || err.code === '23505'
            showToast(isDup ? t('admin.gallery.error.gallery_exists') : err.message, 'error')
        } finally {
            setCreateLoading(false)
        }
    }

    const showToast = (message, type = 'info') => {
        setToast({ show: true, message, type })
        setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3000)
    }

    const showConfirm = (message, onConfirm) => {
        setConfirmDialog({ show: true, message, onConfirm })
    }

    const handleCancelConfirm = () => setConfirmDialog({ show: false, message: '', onConfirm: null })
    const handleConfirm = () => {
        if (confirmDialog.onConfirm) confirmDialog.onConfirm()
        setConfirmDialog({ show: false, message: '', onConfirm: null })
    }

    const handleLogin = async (e) => {
        e.preventDefault()
        if (honeyPot) return
        setAuthLoading(true)
        try {
            const { error } = await supabase.auth.signInWithPassword({ email, password })
            if (error) throw error
            showToast(t('auth.success'), 'success')
        } catch (error) {
            showToast(error.message, 'error')
        } finally {
            setAuthLoading(false)
        }
    }

    const handleLogout = async () => {
        await supabase.auth.signOut()
        setSession(null)
    }

    useEffect(() => {
        if (galleries.length > 0 && !selectedGallery) {
            setSelectedGallery(galleries[0])
        }
    }, [galleries])

    useEffect(() => {
        if (!selectedGallery) return
        setImagesLoading(true)
        fetchGalleryImages(selectedGallery.id)
            .then(data => { setImages(data); setImagesLoading(false) })
            .catch(err => { showToast(t('admin.gallery.error.load') + ': ' + err.message, 'error'); setImagesLoading(false) })
    }, [selectedGallery])

    const handleFiles = async (files) => {
        if (!selectedGallery) return
        setUploading(true)
        let uploadedCount = 0
        for (const file of Array.from(files)) {
            if (!file.type.startsWith('image/')) continue
            try {
                const newImage = await uploadGalleryImage(selectedGallery.id, selectedGallery.service_key, file, '')
                setImages(prev => [...prev, newImage])
                uploadedCount++
            } catch (err) {
                showToast(t('admin.gallery.error.upload') + ': ' + err.message, 'error')
            }
        }
        setUploading(false)
        if (uploadedCount > 0) showToast(t('admin.gallery.success.upload'), 'success')
    }

    const handleDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)
        if (e.dataTransfer.files?.length > 0) handleFiles(e.dataTransfer.files)
    }

    const handleDragOver = (e) => { e.preventDefault(); setDragActive(true) }
    const handleDragLeave = () => setDragActive(false)

    const handleDeleteImage = (img) => {
        showConfirm(t('admin.gallery.confirm_delete_img'), async () => {
            try {
                await deleteGalleryImage(img.id, img.storage_path)
                const remaining = images.filter(i => i.id !== img.id)
                setImages(remaining)
                showToast(t('admin.gallery.success.delete'), 'success')
                // Auto-delete gallery if empty
                if (remaining.length === 0 && selectedGallery) {
                    try {
                        await deleteGallery(selectedGallery.id)
                        refreshGalleries()
                        setSelectedGallery(null)
                        showToast(t('admin.gallery.success.gallery_deleted'), 'success')
                    } catch (err) {
                        showToast(t('admin.gallery.error.gallery_delete') + ': ' + err.message, 'error')
                    }
                }
            } catch (err) {
                showToast(t('admin.gallery.error.delete') + ': ' + err.message, 'error')
            }
        })
    }

    const handleDeleteGallery = (g) => {
        showConfirm(t('admin.gallery.confirm_delete_gallery'), async () => {
            try {
                await deleteGallery(g.id)
                refreshGalleries()
                if (selectedGallery?.id === g.id) setSelectedGallery(null)
                showToast(t('admin.gallery.success.gallery_deleted'), 'success')
            } catch (err) {
                showToast(t('admin.gallery.error.gallery_delete') + ': ' + err.message, 'error')
            }
        })
    }

    const handleReplaceImage = (imgId) => {
        setReplacingImageId(imgId)
        replaceFileInputRef.current?.click()
    }

    const handleReplaceFileChange = async (e) => {
        const file = e.target.files?.[0]
        if (!file || !replacingImageId || !selectedGallery) return
        try {
            const updated = await replaceGalleryImage(replacingImageId, selectedGallery.service_key, file)
            setImages(prev => prev.map(i => i.id === replacingImageId ? updated : i))
            showToast(t('admin.gallery.success.replaced'), 'success')
        } catch (err) {
            showToast(t('admin.gallery.error.replace') + ': ' + err.message, 'error')
        } finally {
            setReplacingImageId(null)
            e.target.value = ''
        }
    }

    const startEditLabel = (img) => {
        setEditingLabelId(img.id)
        setEditingLabelValue(img.label || '')
    }

    const commitLabel = async (imgId) => {
        try {
            await updateGalleryLabel(imgId, editingLabelValue)
            setImages(prev => prev.map(i => i.id === imgId ? { ...i, label: editingLabelValue || null } : i))
            showToast(t('admin.gallery.success.label_saved'), 'success')
        } catch (err) {
            showToast(t('admin.gallery.error.label_save') + ': ' + err.message, 'error')
        } finally {
            setEditingLabelId(null)
        }
    }

    if (!session) {
        return (
            <div className="flex justify-center items-center p-4 min-h-screen bg-cloud">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
                    <div className="p-8 bg-white rounded-2xl border shadow-2xl">
                        <h2 className="mb-6 text-3xl font-bold text-center text-primary">{t('auth.login')}</h2>
                        <form onSubmit={handleLogin} className="space-y-6">
                            <div className="absolute opacity-0 pointer-events-none" aria-hidden="true">
                                <input type="text" value={honeyPot} onChange={(e) => setHoneyPot(e.target.value)} tabIndex="-1" autoComplete="off" />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium text-ash">{t('auth.email')}</label>
                                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="px-4 py-3 w-full rounded-lg border outline-none bg-cloud focus:ring-2 focus:ring-primary" required />
                            </div>
                            <div>
                                <label className="block mb-1 text-sm font-medium text-ash">{t('auth.password')}</label>
                                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="px-4 py-3 w-full rounded-lg border outline-none bg-cloud focus:ring-2 focus:ring-primary" required />
                            </div>
                            <button type="submit" disabled={authLoading} className="flex gap-2 justify-center items-center py-3 w-full font-bold rounded-lg transition text-ink bg-primary hover:shadow-lg disabled:opacity-50">
                                {authLoading ? <div className="w-5 h-5 rounded-full border-2 animate-spin border-ink/30 border-t-ink" /> : t('auth.signIn')}
                            </button>
                        </form>
                    </div>
                </motion.div>
            </div>
        )
    }

    if (roleLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-cloud">
                <div className="w-12 h-12 rounded-full border-b-2 animate-spin border-primary" />
            </div>
        )
    }

    if (!permissions['admin.gallery.access']) {
        return (
            <div className="flex flex-col justify-center items-center p-8 space-y-4 min-h-screen text-center bg-cloud">
                <FaExclamationCircle className="text-5xl text-red-500" />
                <h2 className="text-2xl font-bold text-ink">{t('admin.access.denied')}</h2>
                <p className="text-ash">{t('admin.access.req_super_admin')}</p>
                <button onClick={() => navigate('/admin')} className="flex gap-2 justify-center items-center px-6 py-2 font-medium rounded-lg shadow transition-all bg-primary text-ink hover:bg-primary/90">
                    <FaArrowLeft />
                    {t('common.return_dashboard')}
                </button>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-cloud">
            {/* Header */}
            <header className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
                <div className="flex gap-3 items-center px-4 py-3 mx-auto max-w-7xl">
                    <Link
                        to="/admin"
                        className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-gray-100 text-ash shrink-0"
                        title={t('admin.storage.return')}
                    >
                        <FaArrowLeft />
                    </Link>
                    <h1 className="flex-1 text-xl font-bold text-primary">{t('admin.gallery.title')}</h1>
                    <button
                        onClick={handleLogout}
                        className="p-2 rounded-lg border border-gray-200 transition-all hover:bg-red-600 hover:text-white hover:border-red-600 text-ash shrink-0"
                        title={t('auth.signOut')}
                    >
                        <FaSignOutAlt />
                    </button>
                </div>
                <div className="px-4 pb-2 mx-auto max-w-7xl">
                    <ServiceHealthBar />
                </div>
            </header>

            <div className="flex flex-col gap-4 px-4 py-6 mx-auto max-w-7xl sm:flex-row sm:gap-6">
                {/* Sidebar */}
                <aside className="w-full sm:w-48 shrink-0">
                    <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                            <p className="text-xs font-semibold tracking-wider uppercase text-ash">{t('admin.gallery.sidebar_label')}</p>
                        </div>
                        <div className="py-2">
                            {galleriesLoading ? (
                                <div className="px-4 py-3 text-sm animate-pulse text-ash">Loading...</div>
                            ) : galleries.length === 0 ? (
                                <div className="px-4 py-3 text-sm text-ash">{t('admin.gallery.no_galleries', 'No galleries')}</div>
                            ) : (
                                galleries.map(g => (
                                    <div key={g.id} className={`group relative flex items-center transition-all ${selectedGallery?.id === g.id ? 'bg-teal-50 border-l-2 border-teal-500' : 'hover:bg-gray-50'}`}>
                                        <button
                                            onClick={() => setSelectedGallery(g)}
                                            className={`flex-1 text-left px-4 py-2.5 text-sm font-medium transition-all ${selectedGallery?.id === g.id ? 'text-teal-700' : 'text-ink'}`}
                                        >
                                            {g.service_label}
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDeleteGallery(g) }}
                                            className="p-1 mr-2 text-red-400 rounded opacity-0 transition-all hover:text-red-600 hover:bg-red-50 group-hover:opacity-100"
                                            title={t('admin.actions.delete', 'Delete')}
                                        >
                                            <FaTrash size={10} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="px-3 py-3 border-t border-gray-100">
                            {creatingGallery ? (
                                <div className="space-y-2">
                                    {availableServices.length === 0 ? (
                                        <p className="text-[11px] text-ash italic px-1">{t('admin.gallery.all_services_created')}</p>
                                    ) : (
                                        <select
                                            autoFocus
                                            value={selectedServiceKey}
                                            onChange={(e) => setSelectedServiceKey(e.target.value)}
                                            className="w-full text-xs px-2 py-1.5 rounded border border-teal-300 outline-none focus:ring-1 focus:ring-teal-400 text-ink bg-white"
                                        >
                                            <option value="">{t('admin.gallery.select_service')}</option>
                                            {availableServices.map(s => (
                                                <option key={s.key} value={s.key}>{s.label}</option>
                                            ))}
                                        </select>
                                    )}
                                    <div className="flex gap-1.5">
                                        <button
                                            onClick={handleCreateGallery}
                                            disabled={createLoading || !selectedServiceKey}
                                            className="flex-1 py-1.5 text-xs font-semibold rounded bg-teal-500 text-white hover:bg-teal-600 disabled:opacity-50 transition-colors"
                                        >
                                            {createLoading ? '…' : t('admin.gallery.new_gallery_create')}
                                        </button>
                                        <button
                                            onClick={() => { setCreatingGallery(false); setSelectedServiceKey('') }}
                                            className="px-2 py-1.5 text-xs font-semibold rounded border border-gray-300 text-ash hover:bg-gray-100 transition-colors"
                                        >
                                            {t('common.cancel')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setCreatingGallery(true)}
                                    className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-ash rounded-lg border border-dashed border-gray-300 hover:border-gray-400 hover:text-ink transition-all"
                                >
                                    <FaPlus size={10} />
                                    {t('admin.gallery.new_gallery')}
                                </button>
                            )}
                        </div>
                    </div>
                </aside>

                {/* Main panel */}
                <main className="flex-1 min-w-0">
                    {!selectedGallery ? (
                        <div className="flex justify-center items-center h-64 text-sm text-ash">
                            {t('admin.gallery.select_gallery')}
                        </div>
                    ) : (
                        <div className="space-y-5">
                            {/* Gallery title */}
                            <div className="flex gap-3 items-center">
                                <h2 className="text-lg font-bold text-ink">{selectedGallery.service_label}</h2>
                                <span className="px-2 py-0.5 rounded-full text-xs font-mono font-semibold bg-teal-50 text-teal-700 border border-teal-200">
                                    {selectedGallery.service_key}
                                </span>
                                <span className="text-sm text-ash">{t('admin.gallery.image_count', { count: images.length })}</span>
                            </div>

                            {/* Upload zone */}
                            <div
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onClick={() => !uploading && fileInputRef.current?.click()}
                                className={`relative flex flex-col items-center justify-center gap-3 p-8 rounded-xl border-2 border-dashed cursor-pointer transition-all ${dragActive ? 'bg-teal-50 border-teal-400' : 'bg-white border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="hidden"
                                    onChange={(e) => e.target.files?.length > 0 && handleFiles(e.target.files)}
                                />
                                {uploading ? (
                                    <>
                                        <div className="w-8 h-8 rounded-full border-2 border-teal-400 animate-spin border-t-transparent" />
                                        <p className="text-sm font-medium text-ash">{t('admin.gallery.uploading')}</p>
                                    </>
                                ) : (
                                    <>
                                        <FaUpload className={`text-2xl ${dragActive ? 'text-teal-500' : 'text-gray-400'}`} />
                                        <p className="text-sm font-medium text-ash">{t('admin.gallery.upload_hint')}</p>
                                    </>
                                )}
                            </div>

                            {/* Hidden replace file input */}
                            <input
                                ref={replaceFileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleReplaceFileChange}
                            />

                            {/* Image grid */}
                            {imagesLoading ? (
                                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                                    {Array.from({ length: 8 }).map((_, i) => (
                                        <div key={i} className="aspect-[4/3] rounded-lg bg-gray-200 animate-pulse" />
                                    ))}
                                </div>
                            ) : images.length === 0 ? (
                                <div className="flex flex-col gap-2 justify-center items-center py-16 text-ash">
                                    <FaImages className="text-4xl text-gray-300" />
                                    <p className="text-sm">{t('admin.gallery.no_images')}</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                                    {images.map((img, index) => (
                                        <motion.div
                                            key={img.id}
                                            layout
                                            initial={{ opacity: 0, scale: 0.95 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.9 }}
                                            className="overflow-hidden relative bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow group hover:shadow-md"
                                        >
                                            {/* Thumbnail */}
                                            <button
                                                type="button"
                                                onClick={() => { setPreviewIndex(index); setPreviewOpen(true) }}
                                                className="w-full text-left aspect-[4/3] overflow-hidden bg-gray-100 cursor-zoom-in"
                                            >
                                                <img
                                                    src={img.url}
                                                    alt={img.label || ''}
                                                    className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                                                    loading="lazy"
                                                />
                                            </button>

                                            {/* Replace + Delete buttons */}
                                            <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                                <button
                                                    onClick={() => handleReplaceImage(img.id)}
                                                    className="p-1.5 rounded-md bg-white/90 text-blue-500 hover:bg-blue-600 hover:text-white border border-gray-200 hover:border-blue-600 transition-all shadow-sm"
                                                    title={t('admin.gallery.replace_image', 'Replace image')}
                                                >
                                                    <FaSyncAlt size={11} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteImage(img)}
                                                    className="p-1.5 rounded-md bg-white/90 text-red-500 hover:bg-red-600 hover:text-white border border-gray-200 hover:border-red-600 transition-all shadow-sm"
                                                    title={t('admin.actions.delete', 'Delete')}
                                                >
                                                    <FaTrash size={11} />
                                                </button>
                                            </div>

                                            {/* Label */}
                                            <div className="px-2 py-1.5">
                                                {editingLabelId === img.id ? (
                                                    <input
                                                        autoFocus
                                                        type="text"
                                                        value={editingLabelValue}
                                                        onChange={(e) => setEditingLabelValue(e.target.value)}
                                                        onBlur={() => commitLabel(img.id)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') commitLabel(img.id); if (e.key === 'Escape') setEditingLabelId(null) }}
                                                        className="w-full text-xs px-1.5 py-1 rounded border border-teal-300 outline-none focus:ring-1 focus:ring-teal-400 text-ink"
                                                        placeholder={t('admin.gallery.label_placeholder')}
                                                    />
                                                ) : (
                                                    <button
                                                        onClick={() => startEditLabel(img)}
                                                        className="flex gap-1 items-center w-full text-xs text-left text-ash hover:text-ink group/label"
                                                        title={t('admin.gallery.label_placeholder')}
                                                    >
                                                        <span className="flex-1 truncate">
                                                            {img.label || <span className="italic text-gray-300">{t('admin.gallery.label_placeholder')}</span>}
                                                        </span>
                                                        <FaEdit size={9} className="opacity-0 transition-opacity shrink-0 group-hover/label:opacity-60" />
                                                    </button>
                                                )}
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>

            {/* Confirm Dialog */}
            <AnimatePresence>
                {confirmDialog.show && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="flex fixed inset-0 z-50 justify-center items-center p-4 backdrop-blur-sm bg-black/70"
                        onClick={handleCancelConfirm}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                            transition={{ type: 'spring', duration: 0.3 }}
                            onClick={(e) => e.stopPropagation()}
                            className="p-8 w-full max-w-md bg-white rounded-xl border-2 border-gray-200 shadow-2xl"
                        >
                            <h3 className="mb-4 text-xl font-bold text-ink">{t('admin.confirmAction')}</h3>
                            <p className="mb-6 text-ash">{confirmDialog.message}</p>
                            <div className="flex gap-2 justify-end">
                                <button onClick={handleCancelConfirm} className="px-4 py-2 text-sm font-medium rounded-md border border-gray-200 transition-colors hover:bg-gray-100">
                                    {t('common.cancel')}
                                </button>
                                <button onClick={handleConfirm} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md transition-colors hover:bg-red-700">
                                    {t('common.confirm')}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Toast */}
            <AnimatePresence>
                {toast.show && (
                    <motion.div
                        initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }}
                        className="fixed right-8 bottom-8 z-50"
                    >
                        <div className={`flex items-center gap-3 px-6 py-4 rounded-lg shadow-2xl border backdrop-blur-sm ${toast.type === 'success' ? 'bg-green-50 text-green-900 border-green-200' : toast.type === 'error' ? 'bg-red-50 text-red-900 border-red-200' : 'bg-blue-50 text-blue-900 border-blue-200'}`}>
                            {toast.type === 'success' && <FaCheckCircle className="text-xl text-green-600" />}
                            {toast.type === 'error' && <FaExclamationCircle className="text-xl text-red-600" />}
                            {toast.type === 'info' && <FaInfoCircle className="text-xl text-blue-600" />}
                            <span className="font-medium">{toast.message}</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            <GalleryModal
                images={images.map(img => img.url)}
                isOpen={previewOpen}
                onClose={() => setPreviewOpen(false)}
                initialIndex={previewIndex}
            />
        </div>
    )
}
